import React, { useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import BlogLayout from '@/components/admin/BlogLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save } from 'lucide-react';

interface Category {
  id?: number;
  name: string;
  slug: string;
  description: string;
}

interface CategoryFormProps {
  category?: Category;
  isEditing?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ category, isEditing = false }) => {
  const { errors } = usePage().props as any;
  
  const [formData, setFormData] = useState({
    name: category?.name || '',
    slug: category?.slug || '',
    description: category?.description || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name if creating new
    if (field === 'name' && !isEditing) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    const url = isEditing && category?.id 
      ? `/admin/blog/categories/${category.id}` 
      : '/admin/blog/categories';
    
    const method = isEditing && category?.id ? 'put' : 'post';

    router.visit(url, {
      method: method as 'post' | 'put' | 'patch',
      data: { blog_category: formData },
      onFinish: () => setIsSubmitting(false),
    });
  };

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Blog', href: '/admin/blog/posts' },
    { label: 'Categories', href: '/admin/blog/categories' },
    { label: isEditing ? 'Edit Category' : 'New Category' },
  ];

  return (
    <BlogLayout breadcrumbs={breadcrumbs}>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link href="/admin/blog/categories">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? 'Edit Category' : 'New Category'}
            </h1>
            <p className="text-muted-foreground">
              {isEditing ? 'Update your category' : 'Create a new category'}
            </p>
          </div>
        </div>

        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Category Details</CardTitle>
              <CardDescription>
                Enter the details for your category
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Category name"
                />
                {errors?.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleChange('slug', e.target.value)}
                  placeholder="category-slug"
                />
                {errors?.slug && (
                  <p className="text-sm text-red-500">{errors.slug}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  URL-friendly identifier
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief description of this category"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" asChild>
                  <Link href="/admin/blog/categories">Cancel</Link>
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting}>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditing ? 'Update Category' : 'Create Category'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </BlogLayout>
  );
};

export default CategoryForm;
